/** "Losses in the pipeline" (Bank › Books › Loans): the bank's recognised-but-not-yet-charged-off stress losses, per book, at the start of the year. */
import type { BookCreditState, BookId, ProductDef } from "@bsim/domain";
import { formatUsdM } from "../format";
import { GxNotes } from "../theme/GxNotes";
import { GxCard } from "../theme/primitives";
import { GxDataTable, type TableRow } from "./DataTable";
import { backlogText, pipelineRows } from "./creditPipeline";

export function CreditPipeline({ credit, products, asOf }: { readonly credit: Partial<Record<BookId, BookCreditState>>; readonly products: readonly ProductDef[]; readonly asOf: string }) {
  const rows = pipelineRows(credit, products);
  const table: TableRow[] = rows.map((r) => ({ id: r.book, label: r.label, cells: [r.worst, backlogText(r), r.nonaccrualUsd === 0 ? null : formatUsdM(r.nonaccrualUsd)] }));
  return (
    <GxCard title="Losses in the pipeline" aside={<span className="gx-small gx-faint">{asOf}</span>} className="gx-fin-pipeline" testId="financials.pipeline">
      <div className="gx-card-b">
        {rows.length === 0 ? <p className="gx-muted gx-small" data-testid="financials.pipeline.none">Nothing is waiting to be charged off: no loan book&apos;s drivers have fallen far enough yet to book a stress loss.</p> : <>
          <GxDataTable testId="financials.pipeline.table" caption="Losses already booked and still to be charged off, by loan book" firstColumn="Book" columns={["Worst so far", "Still to charge off", "Behind it on non-accrual"]} rows={table} dense />
          <GxNotes notes={["A loss is booked once, as the property or jobs picture worsens past each book's cushion, and then charged off over the next two to three years. What is booked and not yet charged off is reserved in full."]} />
        </>}
      </div>
    </GxCard>
  );
}
